/**
 * Création / réglages d'une campagne d'appel (admin).
 *
 * La cible se compose avec les MÊMES filtres que l'écran Entreprises — pas
 * un second langage de ciblage à apprendre — et l'aperçu dit combien
 * d'entreprises seraient appelées avant qu'on lance quoi que ce soit. Une
 * fois lancée, la cible est figée : on ne modifie plus que ce qui ne change
 * pas qui l'on appelle (nom, consigne, affectés, objectif, retentatives).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Users } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import Modal from "@/crm/components/Modal";
import CompanyFilters, { EMPTY_FILTERS, filtersToParams, hydrateFilters } from "@/crm/components/CompanyFilters";

const FIELD = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500";
const LABEL = "mb-1 block text-xs text-slate-500";

export default function CallCampaignForm({ campaign, companyIds, initialFilters, onClose, onSaved }) {
  const frozen = !!campaign && campaign.status !== "draft";
  // Cible par sélection : fiches cochées dans la liste, ou la fiche ouverte.
  // Les filtres ne servent alors à rien — on montre le compte, pas le panneau.
  const manualIds = companyIds || campaign?.company_ids || [];
  const manual = manualIds.length > 0;
  const [meta, setMeta] = useState({ orders: {}, metrics: {}, retry_default: { max_attempts: 3, delay_hours: 24 } });
  const [solutions, setSolutions] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [name, setName] = useState(campaign?.name || "");
  const [consigne, setConsigne] = useState(campaign?.consigne || "");
  const [solutionId, setSolutionId] = useState(campaign?.solution_id || "");
  const [order, setOrder] = useState(campaign?.order || "score");
  const [metric, setMetric] = useState(campaign?.objective?.metric || "rdv");
  const [target, setTarget] = useState(campaign?.objective?.target ?? 20);
  const [maxAttempts, setMaxAttempts] = useState(campaign?.retry?.max_attempts ?? 3);
  const [delayHours, setDelayHours] = useState(campaign?.retry?.delay_hours ?? 24);
  const [assignees, setAssignees] = useState(campaign?.assignees || []);
  const [hours, setHours] = useState(campaign?.hours || { enabled: false, start: "09:00", end: "18:00", days: [1, 2, 3, 4, 5] });
  const [quota, setQuota] = useState(campaign?.quota_per_day ?? 0);
  // Cible : état de filtres de l'écran Entreprises, relu depuis la campagne.
  // `initialFilters` : la liste d'entreprises a été sélectionnée « toutes les
  // pages » — la campagne hérite de ses filtres plutôt que d'une liste d'ids.
  const initial = useMemo(() => {
    const sp = new URLSearchParams(campaign?.filters || initialFilters || {});
    const f = hydrateFilters(sp);
    // `activity` est un seul menu à l'écran mais deux paramètres d'API
    // (voir filtersToParams) : on refait le chemin inverse pour la relecture.
    if (sp.get("never_contacted") === "true") f.activity = "never";
    else if (sp.get("inactive_days")) f.activity = sp.get("inactive_days");
    return { filters: f, search: sp.get("search") || "" };
  }, [campaign, initialFilters]);
  const [filters, setFilters] = useState(initial.filters);
  const [search, setSearch] = useState(initial.search);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/call-campaigns/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/solutions").then(({ data }) => setSolutions(data || [])).catch(() => {});
    api.get("/users/commercials").then(({ data }) => setCommercials(data || [])).catch(() => {});
  }, []);

  const payload = useCallback(() => ({
    name, consigne, solution_id: solutionId || null,
    filters: manual ? {} : filtersToParams(filters, search), company_ids: manualIds, order,
    objective: { metric, target: Number(target) || 0 },
    retry: { max_attempts: Number(maxAttempts) || 3, delay_hours: Number(delayHours) || 24 },
    hours, quota_per_day: Number(quota) || 0,
    assignees,
  }), [name, consigne, solutionId, filters, search, manual, manualIds, order, metric, target, maxAttempts, delayHours, hours, quota, assignees]);

  // Aperçu de la cible à chaque changement de filtre ou d'affectés (les
  // fiches suivies par un commercial hors campagne sont écartées).
  useEffect(() => {
    if (frozen) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/call-campaigns/preview", { ...payload(), name: name || "aperçu" });
        setPreview(data);
      } catch { setPreview(null); }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [payload, frozen, name]);

  const toggleAssignee = (id) =>
    setAssignees((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    if (!name.trim()) return toast.error("Donnez un nom à la campagne");
    if (assignees.length === 0) return toast.error("Affectez au moins un commercial");
    setSaving(true);
    try {
      const body = payload();
      const { data } = campaign
        ? await api.put(`/call-campaigns/${campaign.id}`, body)
        : await api.post("/call-campaigns", body);
      toast.success(campaign ? "Campagne mise à jour" : "Campagne créée — lancez-la quand la cible vous convient");
      onSaved(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} size="5xl" testid="call-campaign-form" label="Campagne d'appel">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-heading text-lg font-bold text-slate-900">
          {campaign ? `Réglages — ${campaign.name}` : "Nouvelle campagne d'appel"}
        </h3>
        <button onClick={onClose} aria-label="Fermer"><X size={18} /></button>
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_320px]">
        {/* Colonne principale : identité et cible */}
        <div className="space-y-4">
          <div>
            <label className={LABEL} htmlFor="cc-name">Nom</label>
            <input id="cc-name" data-testid="cc-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Restaurants Lyon — rentrée" className={FIELD} />
          </div>
          <div>
            <label className={LABEL} htmlFor="cc-consigne">Consigne pour les commerciaux</label>
            <textarea id="cc-consigne" data-testid="cc-consigne" rows={3} value={consigne}
              onChange={(e) => setConsigne(e.target.value)}
              placeholder="Angle d'attaque, offre du moment, objection à anticiper…" className={FIELD} />
          </div>
          <div>
            <label className={LABEL} htmlFor="cc-solution">Solution à proposer</label>
            <select id="cc-solution" data-testid="cc-solution" value={solutionId} disabled={frozen}
              onChange={(e) => setSolutionId(e.target.value)} className={FIELD}>
              <option value="">— Aucune en particulier —</option>
              {solutions.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
            <p className="mt-1 text-[11.5px] text-slate-400">
              Pré-retenue sur les fiches qui n'en ont pas encore ; le copilote et « Préparer mon appel » s'alignent.
            </p>
          </div>

          <div>
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-xs text-slate-500">Cible</span>
              {frozen && <span className="text-[11.5px] text-amber-700">Figée depuis le lancement</span>}
            </div>
            {manual ? (
              <div data-testid="cc-manual-target" className="mb-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                Sélection manuelle : <b className="font-mono">{manualIds.length}</b> entreprise{manualIds.length > 1 ? "s" : ""} cochée{manualIds.length > 1 ? "s" : ""}.
              </div>
            ) : (
              <div className={frozen ? "pointer-events-none opacity-60" : ""}>
                <CompanyFilters filters={filters} onChange={setFilters} search={search} onSearch={setSearch}
                  commercials={commercials} isManager isAdmin={false} onExport={() => {}}
                  onReset={() => { setFilters(EMPTY_FILTERS); setSearch(""); }} />
              </div>
            )}
            {!frozen && (
              <div data-testid="cc-preview" className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-sm text-indigo-800">
                {preview ? (
                  <>
                    <b className="font-mono">{preview.count}</b> entreprise{preview.count > 1 ? "s" : ""} à appeler
                    {preview.ecartees_suivies_ailleurs > 0 && (
                      <span className="text-indigo-600"> · {preview.ecartees_suivies_ailleurs} écartée{preview.ecartees_suivies_ailleurs > 1 ? "s" : ""} (suivies par un commercial hors campagne)</span>
                    )}
                    <span className="text-indigo-500"> · sans numéro, déjà clientes et en opposition exclues d'office</span>
                  </>
                ) : <span className="flex items-center gap-2 text-indigo-500"><Loader2 size={13} className="animate-spin" /> Calcul de la cible…</span>}
              </div>
            )}
          </div>
        </div>

        {/* Colonne réglages */}
        <div className="space-y-4">
          <div>
            <label className={LABEL}><Users size={12} className="mr-1 inline" />Commerciaux affectés</label>
            <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2" data-testid="cc-assignees">
              {commercials.length === 0 && <p className="text-xs text-slate-400">Aucun commercial actif.</p>}
              {commercials.map((u) => (
                <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-slate-50">
                  <input type="checkbox" checked={assignees.includes(u.id)} onChange={() => toggleAssignee(u.id)} />
                  <span className="truncate">{u.name}</span>
                  <span className="ml-auto text-[10px] uppercase text-slate-400">{u.role}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className={LABEL} htmlFor="cc-order">Ordre de passage</label>
            <select id="cc-order" data-testid="cc-order" value={order} disabled={frozen}
              onChange={(e) => setOrder(e.target.value)} className={FIELD}>
              {Object.entries(meta.orders || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Objectif</label>
            <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2">
              <input type="number" min="0" data-testid="cc-target" value={target}
                onChange={(e) => setTarget(e.target.value)} className={FIELD} />
              <select data-testid="cc-metric" value={metric} onChange={(e) => setMetric(e.target.value)} className={FIELD}>
                {Object.entries(meta.metrics || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-500">
              <input type="checkbox" data-testid="cc-hours-enabled" checked={!!hours.enabled}
                onChange={(e) => setHours({ ...hours, enabled: e.target.checked })} />
              Plage horaire d'appel (heure de Paris)
            </label>
            {hours.enabled && (
              <div className="mt-1.5 space-y-1.5" data-testid="cc-hours">
                <div className="grid grid-cols-2 gap-2">
                  <input type="time" value={hours.start} onChange={(e) => setHours({ ...hours, start: e.target.value })} className={FIELD} />
                  <input type="time" value={hours.end} onChange={(e) => setHours({ ...hours, end: e.target.value })} className={FIELD} />
                </div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(meta.days || { 1: "lun", 2: "mar", 3: "mer", 4: "jeu", 5: "ven", 6: "sam", 7: "dim" }).map(([d, label]) => {
                    const n = Number(d);
                    const on = (hours.days || []).includes(n);
                    return (
                      <button key={d} type="button" aria-pressed={on}
                        onClick={() => setHours({ ...hours, days: on ? hours.days.filter((x) => x !== n) : [...hours.days, n].sort() })}
                        className={`rounded-md border px-2 py-1 text-xs ${on ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-500"}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-400">Hors plage, « Appeler » et « Résultat » sont indisponibles.</p>
              </div>
            )}
          </div>
          <div>
            <label className={LABEL} htmlFor="cc-quota">Quota d'appels par jour et par commercial</label>
            <input id="cc-quota" type="number" min="0" data-testid="cc-quota" value={quota}
              onChange={(e) => setQuota(e.target.value)} className={FIELD} />
            <span className="text-[11px] text-slate-400">0 = sans limite. L'encadrement n'est pas soumis au quota.</span>
          </div>
          <div>
            <label className={LABEL}>Retentatives (« Pas de réponse »)</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input type="number" min="1" max="10" data-testid="cc-max-attempts" value={maxAttempts}
                  onChange={(e) => setMaxAttempts(e.target.value)} className={FIELD} />
                <span className="text-[11px] text-slate-400">tentatives max.</span>
              </div>
              <div>
                <input type="number" min="1" data-testid="cc-delay-hours" value={delayHours}
                  onChange={(e) => setDelayHours(e.target.value)} className={FIELD} />
                <span className="text-[11px] text-slate-400">heures entre deux</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">Annuler</button>
        <button data-testid="cc-save" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          {saving && <Loader2 size={14} className="animate-spin" />}
          {campaign ? "Enregistrer" : "Créer la campagne"}
        </button>
      </div>
    </Modal>
  );
}
